import React, { useState, useEffect } from 'react';
import { Project } from '../types';

export interface AutoSelectIntentOptions {
  productIds: string[];
  intentsPerProduct: number;
}

interface AutoSelectIntentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (options: AutoSelectIntentOptions) => void;
  project: Project;
  preselectedProductId?: string; // For single-product auto-select
}

const AutoSelectIntentModal: React.FC<AutoSelectIntentModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  project,
  preselectedProductId
}) => {
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [intentsPerProduct, setIntentsPerProduct] = useState<number>(3);

  // Initialize with all products selected or just the preselected one
  useEffect(() => {
    if (isOpen && project) {
      if (preselectedProductId) {
        // Single product mode - only select that product
        setSelectedProductIds([preselectedProductId]);
      } else {
        // All products mode - select all products
        setSelectedProductIds(project.products.map(p => p.id));
      }
    }
  }, [isOpen, project, preselectedProductId]);

  if (!isOpen) return null;

  const handleProductToggle = (productId: string) => {
    setSelectedProductIds(prev =>
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const handleSelectAllProducts = () => {
    if (preselectedProductId) {
      // In single product mode, only allow that product
      setSelectedProductIds([preselectedProductId]);
    } else {
      setSelectedProductIds(project.products.map(p => p.id));
    }
  };

  const handleDeselectAllProducts = () => {
    if (!preselectedProductId) {
      setSelectedProductIds([]);
    }
  };

  const handleConfirm = () => {
    if (selectedProductIds.length === 0) {
      alert('Please select at least one product');
      return;
    }
    if (intentsPerProduct < 1 || intentsPerProduct > 20) {
      alert('Number of intents per product must be between 1 and 20');
      return;
    }

    const options: AutoSelectIntentOptions = {
      productIds: selectedProductIds,
      intentsPerProduct
    };

    onConfirm(options);
    onClose();
  };

  const allProductsSelected = preselectedProductId
    ? selectedProductIds.includes(preselectedProductId)
    : selectedProductIds.length === project.products.length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-primary-900">
            Auto Select Intent
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>

        <div className="space-y-6">
          {/* Product Selection */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-primary-900">
                Select Products
                {preselectedProductId && (
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    (Single product mode)
                  </span>
                )}
              </h3>
              {!preselectedProductId && (
                <div className="flex space-x-2">
                <button
                  onClick={handleSelectAllProducts}
                  className="text-sm text-accent-500 hover:text-accent-600"
                  disabled={allProductsSelected}
                >
                  Select All
                </button>
                <span className="text-gray-300">|</span>
                <button
                  onClick={handleDeselectAllProducts}
                  className="text-sm text-gray-600 hover:text-gray-700"
                  disabled={selectedProductIds.length === 0}
                >
                  Deselect All
                </button>
              </div>
              )}
            </div>
            <div className="border border-gray-200 rounded-lg p-3 max-h-48 overflow-y-auto">
              <div className="space-y-2">
                {project.products.map(product => {
                  const isSelected = selectedProductIds.includes(product.id);
                  const isDisabled = preselectedProductId !== undefined && product.id !== preselectedProductId;
                  return (
                    <label
                      key={product.id}
                      className={`flex items-center space-x-2 p-2 rounded hover:bg-gray-50 ${
                        isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleProductToggle(product.id);
                        }}
                        disabled={isDisabled}
                        className="rounded border-gray-300 text-accent-500 focus:ring-accent-500 cursor-pointer"
                      />
                      <span className="text-sm text-gray-700">{product.name}</span>
                      {isDisabled && (
                        <span className="text-xs text-gray-400">(locked)</span>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {selectedProductIds.length} of {project.products.length} products selected
            </p>
          </div>

          {/* Intents Per Product */}
          <div>
            <h3 className="text-lg font-semibold text-primary-900 mb-3">
              Number of Intents Per Product
            </h3>
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center space-x-4">
                <label className="text-sm text-gray-700 font-medium">
                  Select top
                </label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={intentsPerProduct}
                  onChange={(e) => setIntentsPerProduct(parseInt(e.target.value) || 1)}
                  className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                />
                <label className="text-sm text-gray-700 font-medium">
                  intents per product
                </label>
              </div>
              <p className="text-xs text-gray-500 mt-3">
                The system will automatically select {intentsPerProduct} intent{intentsPerProduct !== 1 ? 's' : ''} 
                {' '}to maximize total keyword coverage. The first intent selected has the most overlap 
                with other categories, and each additional intent adds the most new keywords not yet covered.
              </p>
            </div>
          </div>

          {/* Preview Summary */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-blue-900 mb-2">How it works:</h4>
            <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
              <li>First intent: Selects the intent with the most keyword overlap across other categories (broadest coverage)</li>
              <li>Remaining intents: Greedily selects intents that add the most NEW keywords not yet covered</li>
              <li>Result: {intentsPerProduct} intent{intentsPerProduct !== 1 ? 's' : ''} that maximize total unique keyword coverage</li>
              <li>All keywords under the selected intents are automatically selected</li>
            </ul>
          </div>

          {/* Preview */}
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-sm text-gray-700">
              <span className="font-medium">Preview:</span> Will auto-select the top{' '}
              <span className="font-semibold">{intentsPerProduct}</span> intent
              {intentsPerProduct !== 1 ? 's' : ''} for{' '}
              <span className="font-semibold">{selectedProductIds.length}</span> product
              {selectedProductIds.length !== 1 ? 's' : ''} based on keyword overlap analysis.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              className="px-4 py-2 bg-blue-700 text-white rounded-lg text-sm font-medium hover:bg-blue-800 transition-colors"
            >
              Apply Intent Selection
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutoSelectIntentModal;

